import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await authAPI.me();
      setUser(response.data);
      setError(null);
    } catch (err) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
      setError(err.response?.data?.detail || 'Session expired');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      const { access_token, refresh_token, must_change_password } = response.data;

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);

      if (must_change_password) {
        setMustChangePassword(true);
      }

      await fetchUser();
      return { success: true, mustChangePassword: must_change_password };
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const clearMustChangePassword = () => {
    setMustChangePassword(false);
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      // Best effort - still clear local state even if API call fails
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    setMustChangePassword(false);
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    mustChangePassword,
    clearMustChangePassword,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'SUPER_ADMIN',
    isTenantAdmin: user?.role === 'TENANT_ADMIN',
    isOperator: ['SUPER_ADMIN', 'TENANT_ADMIN', 'SUPERVISOR', 'OPERATOR'].includes(user?.role),
    canManageSensors: ['SUPER_ADMIN', 'TENANT_ADMIN', 'SUPERVISOR'].includes(user?.role),
    canManageUsers: ['SUPER_ADMIN', 'TENANT_ADMIN'].includes(user?.role),
    canManageRules: ['SUPER_ADMIN', 'TENANT_ADMIN'].includes(user?.role)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
